import React from 'react';
import './Legal.css';

function Privacy() {
  return (
    <div className="legal-page">
      <div className="container">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last updated: August 9, 2026</p>

        <section>
          <h2>Introduction</h2>
          <p>
            This Privacy Policy describes how ChapterChat, operated by ShareSpace Apps ("we," "our," or "us"),
            collects, uses, and protects your personal information when you use the ChapterChat mobile
            application and website (the "Service"). By using the Service, you agree to the practices
            described in this policy.
          </p>
        </section>

        <section>
          <h2>Information We Collect</h2>

          <h3>Account Information</h3>
          <ul>
            <li><strong>Email Address:</strong> Required to create an account, sign in, and recover your password. Your email is never visible to other users.</li>
            <li><strong>Username:</strong> A display name you choose, visible to other users.</li>
            <li><strong>Profile Details (optional):</strong> Reading preferences, reading goals, favorite books, and other profile information you choose to add.</li>
          </ul>

          <h3>Reading and Book Data</h3>
          <ul>
            <li><strong>Your Library:</strong> Books you add, along with reading status (want to read, reading, finished), progress, ratings, and reviews.</li>
            <li><strong>Reading Activity:</strong> Reading sessions, streaks, and statistics you generate by using the app.</li>
          </ul>

          <h3>Social and Community Data</h3>
          <ul>
            <li><strong>Friends:</strong> Friend connections and friend requests you make.</li>
            <li><strong>Book Clubs:</strong> Clubs you join and discussion posts you write.</li>
            <li><strong>Activity and Comments:</strong> Posts, comments, and reactions you share with the community.</li>
          </ul>

          <h3>What We Do NOT Collect</h3>
          <ul>
            <li>No location or GPS data</li>
            <li>No access to your contacts, calendar, or health data</li>
            <li>No advertising identifiers or third-party analytics or tracking SDKs</li>
            <li>No payment card details (purchases are handled entirely by Apple)</li>
          </ul>
        </section>

        <section>
          <h2>Camera and Photos</h2>
          <p>
            ChapterChat can use your device's camera to scan book barcodes, and your camera or photo library
            to identify books from a photo of your shelf. Camera access is used only while you are actively
            scanning. Barcode scanning happens entirely on your device — only the resulting ISBN is used to
            look up book details. Shelf photos you take or select are analyzed to identify book titles and
            are not stored on our servers or shared with other users.
          </p>
        </section>

        <section>
          <h2>How We Use Your Information</h2>
          <ul>
            <li><strong>Provide the Service:</strong> Maintain your account, sync your library across devices, and power social features like friends, activity feeds, and book clubs.</li>
            <li><strong>AI Features:</strong> Generate personalized book recommendations, reading insights, and discussion prompts (see "AI Features" below).</li>
            <li><strong>Notifications:</strong> If you enable reading reminders, notifications are scheduled locally on your device. We do not send remote push notifications.</li>
            <li><strong>Safety:</strong> Moderate reported content and keep the community safe.</li>
          </ul>
        </section>

        <section>
          <h2>AI Features</h2>
          <p>
            Some features — such as book recommendations, shelf analysis, reading insights, and book club
            discussion prompts — are powered by Google's Gemini API. When you use these features, relevant
            information (such as book titles from your library or your stated reading preferences) is sent
            to Google to generate a response. We do not send your email address or password to the AI
            service, and we do not use your data to train AI models.
          </p>
        </section>

        <section>
          <h2>Third-Party Services</h2>
          <ul>
            <li><strong>Firebase (Google):</strong> We use Firebase Authentication and Cloud Firestore to manage accounts and securely store your data. Analytics and advertising features of Firebase are disabled.</li>
            <li><strong>Google Books and OpenLibrary:</strong> When you search for a book or scan a barcode, your search query or ISBN is sent to these services to retrieve book details and cover images. No personal information is included.</li>
            <li><strong>Google Gemini API:</strong> Used for AI features as described above.</li>
            <li><strong>Apple App Store:</strong> Subscriptions and payments are processed by Apple. We never see or store your payment details.</li>
          </ul>
          <p>We do not sell, rent, or trade your personal information to anyone.</p>
        </section>

        <section>
          <h2>Subscriptions</h2>
          <p>
            ChapterChat offers optional auto-renewable subscriptions (ChapterChat Plus) billed through your
            Apple ID. Payment, renewal, and cancellation are managed by Apple in your App Store account
            settings. We only receive confirmation of your subscription status so we can unlock Plus
            features.
          </p>
        </section>

        <section>
          <h2>What Other Users Can See</h2>
          <ul>
            <li><strong>Username and profile:</strong> Visible to other signed-in users so friends can find you. Your email address is never visible.</li>
            <li><strong>Bookshelf:</strong> Visible only to friends you have accepted, and only if you enable shelf sharing.</li>
            <li><strong>Activity:</strong> You control whether your reading activity is shared to the feed. Private activity is hidden from others.</li>
            <li><strong>Book club discussions:</strong> Visible only to members of that club.</li>
            <li><strong>Buddy reads:</strong> Shared only between you and your reading partner.</li>
          </ul>
          <p>
            You can adjust these choices at any time in the app's settings, including whether your profile
            is discoverable, whether your activity is shared, and whether friends can see your shelf.
          </p>
        </section>

        <section>
          <h2>Data Storage and Security</h2>
          <ul>
            <li>Your data is stored in Google Firebase (Cloud Firestore) data centers, primarily located in the United States.</li>
            <li>All data is encrypted in transit (HTTPS/TLS) and at rest.</li>
            <li>Access to your data is restricted by security rules — for example, only you can modify your library, and only accepted friends can view your shared shelf.</li>
            <li>Passwords are handled by Firebase Authentication and are never stored in plain text.</li>
          </ul>
        </section>

        <section>
          <h2>Data Retention and Account Deletion</h2>
          <p>
            We keep your data for as long as your account is active. You can permanently delete your account
            at any time from the app's Settings. Deleting your account removes your profile, library,
            activity, friend connections, and club memberships from our servers. Some minimal records may be
            retained where required by law.
          </p>
        </section>

        <section>
          <h2>Your Rights</h2>
          <p>Depending on where you live, you may have the right to:</p>
          <ul>
            <li>Access a copy of the personal data we hold about you</li>
            <li>Correct inaccurate information</li>
            <li>Delete your account and associated data</li>
            <li>Object to or restrict certain processing</li>
          </ul>
          <p>
            You can exercise most of these rights directly in the app (edit your profile, adjust sharing
            settings, or delete your account), or by contacting us using the details below.
          </p>
        </section>

        <section>
          <h2>Children's Privacy</h2>
          <p>
            ChapterChat is not directed at children under 13, and we do not knowingly collect personal
            information from children under 13. If you believe a child has created an account, please
            contact us and we will delete the account and its data.
          </p>
        </section>

        <section>
          <h2>Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. When we do, we will update the "Last
            updated" date above and, for significant changes, notify you in the app. Continued use of the
            Service after changes take effect constitutes acceptance of the updated policy.
          </p>
        </section>

        <section>
          <h2>Contact Us</h2>
          <p>
            If you have questions about this Privacy Policy or how your data is handled, contact us at:
          </p>
          <ul>
            <li><strong>Email:</strong> <a href="mailto:admin@sharespaceapps.com">admin@sharespaceapps.com</a></li>
          </ul>
        </section>
      </div>
    </div>
  );
}

export default Privacy;
